<template>
  <div class="vacancy-where-you">
    <VRow class="d-none d-md-flex">
      <VCol
        cols="12"
        md="6"
        lg="3"
      >
        <VCard
          outlined
          height="100%"
          class="preset-ai pa-4"
        >
          <div class="cursor-pointer">
            <div
              class="preset-ai__action preset-ai__action--done"
              v-if="aiQuestionsTemplateGenerated"
            >
              {{ $t('selectionQOver') }}
            </div>
            <div class="preset-ai__action" v-else>
              {{ $t('recommended') }}
            </div>
            <VCardTitle class="preset-title d-flex align-center pa-0 mb-2">
              <span class="mr-2">{{ $t('aiInterview') }}</span>
              <div @click="openFaqInterview" class="cursor-pointer">
                <img :src="require('@/assets/img/faq.svg')" />
              </div>
            </VCardTitle>
            <VCardSubtitle class="pa-0 mt-md-0" @click="setPreset('aiInterview')">
              {{ $t('aiInterviewDescription') }}
            </VCardSubtitle>
          </div>
        </VCard>
      </VCol>
      <VCol
        v-for="(preset, index) in presets"
        :key="index"
        cols="12"
        md="6"
        lg="3"
      >
        <VCard
          @click="setPreset(preset)"
          outlined
          height="100%"
          class="pa-4"
        >
          <VCardTitle class="preset-title d-flex align-start pa-0 mb-2">
            {{ preset.title }}
          </VCardTitle>
          <VCardSubtitle v-if="preset.description" class="pa-0 mt-md-0">
            {{ preset.description }}
          </VCardSubtitle>
          <WizardCounts
            short
            v-if="preset.counts.questions > 0"
            :questions="preset.counts.questions"
            class="mb-0 mt-0 ml-auto"
          />
        </VCard>
      </VCol>
    </VRow>
    <div
      class="position-relative d-md-none white pa-2 pr-6 mb-1"
    >
      <div class="preset-title flex-column cursor-pointer">
        <div
          v-if="vacancy.hasAiQuestions
            && vacancy.aiQuestionsGenerated"
          class="preset-ai__action preset-ai__action--done preset-ai__action-mobile"
        >
          {{ $t('selectionQOver') }}
        </div>
        <div v-else class="preset-ai__action preset-ai__action-mobile">
          {{ $t('recommended') }}
        </div>
        <VCardTitle class="preset-title d-flex align-center pa-0 mb-2">
          <span class="mr-2">{{ $t('aiInterview') }}</span>
          <div
            @click="openFaqInterview"
            class="cursor-pointer"
          >
            <img :src="require('@/assets/img/faq.svg')" />
          </div>
        </VCardTitle>
      </div>
      <div @click="setPreset('aiInterview')">
        {{ $t('aiInterviewDescription') }}
      </div>
      <div class="template-arrow-icon">
        <VIcon
          color="grey lighten-2"
        >
          mdi-chevron-right
        </VIcon>
      </div>
    </div>
    <div
      v-for="(preset, index) in presets"
      :key="index"
      class="position-relative d-md-none white pa-2 pr-4 mb-1"
      @click="setPreset(preset)"
    >
      <div class="preset-title flex-column">
        {{ preset.title }}
        <WizardCounts short v-if="preset.counts.questions > 0" :questions="preset.counts.questions" />
      </div>
      <div v-if="preset.description">
        {{ preset.description }}
      </div>
      <div class="template-arrow-icon">
        <VIcon
          color="grey lighten-2"
        >
          mdi-chevron-right
        </VIcon>
      </div>
    </div>
  </div>
</template>

<script>
import WizardCounts from '@/components/Vacancy/WizardCounts';
import useGlobalEventEmitter from '@/compositions/useGlobalEventEmitter';
import { vacancyTemplateTypes } from '@/enums';
import {
  onBeforeUnmount, toRefs, ref, onMounted, computed,
} from '@vue/composition-api';
import useAccount from '@/compositions/accounting/useAccount';

export default {
  name: 'VacancyNewTemplateList',
  components: { WizardCounts },
  props: {
    presets: {
      type: Array,
      default: () => [],
    },
    vacancy: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { presets, vacancy } = toRefs(props);
    const { subscribe } = useGlobalEventEmitter('presets');
    const { fetch: fetchAccount } = useAccount();
    const tariffDialog = ref(false);
    let unsubscribeHandler = null;

    const aiQuestionsTemplateGenerated = computed(() => vacancy.value.hasAiQuestions
      && vacancy.value.aiQuestionsGenerated && !vacancy.value.aiInterviewPresetId);

    function setPreset(preset) {
      emit('select', preset);
    }

    function openFaqInterview() {
      emit('faqAiInterview');
    }

    unsubscribeHandler = subscribe('setPreset', (type) => {
      switch (type) {
        case vacancyTemplateTypes.SCRATCH:
          setPreset(presets.value[0]);
          break;
        case vacancyTemplateTypes.EXPRESS:
          setPreset(presets.value[1]);
          break;
        default:
          throw Error('No preset type');
      }
    });

    onBeforeUnmount(() => {
      if (typeof unsubscribeHandler === 'function') {
        unsubscribeHandler();
      }
    });

    onMounted(async () => {
      await fetchAccount();
    });

    return {
      setPreset,
      tariffDialog,
      openFaqInterview,
      aiQuestionsTemplateGenerated,
    };
  },
};
</script>

<style lang="scss" scoped>
  .position-relative {
    position: relative;
    padding-right: 30px;
  }
  .preset-title {
    min-height: 25px;

    @media #{map-get($display-breakpoints, 'sm-and-down')} {
      position: relative;
      display: flex;
      font-size: 18px;
      line-height: 30px;
      color: #333333;
      font-weight: normal;
    }
  }

  .preset-ai {
    border-radius: 5px;
    &__action {
      border-radius: 3px;
      background: #1F85D7;
      color: #FFF;
      font-size: 11px;
      font-weight: 600;
      line-height: 160%;
      letter-spacing: 0.24px;
      text-transform: uppercase;
      position: absolute;
      top: -12px;
      padding: 3px 7px;
      white-space: nowrap;
    }
    &__action--done {
      background: #1fc57f;
    }
    &__action-mobile {
      top: 0;
      left: 25%;
      @media screen and (max-width: 900px) {
        right: 0;
        left: auto;
        top: -20px;
      }
    }
  }
</style>

<i18n locale="en" lang="yaml">
aiInterview: Questions from AI
aiInterviewDescription: Artificial Intelligence will select and voice the questions for your job. You can make changes if necessary.
recommended: Recommended
selectionQOver: The selection of questions is over
</i18n>

<i18n locale="ru" lang="yaml">
aiInterview: Вопросы от ИИ
aiInterviewDescription: Искусственный Интеллект сам подберет и озвучит вопросы для вашей вакансии. Вы сможете внести изменения, при необходимости.
recommended: Рекомендовано
selectionQOver: Подбор вопросов окончен
</i18n>
